import { useEffect, useState } from "react";
// "@/lib/api", not "./api": the alias is what the Jest setup maps to a mock. A relative
// path resolves the real module, which pulls axios's ESM build into a CommonJS test
// runtime and fails the whole suite at parse time. Same convention as TenantContext.jsx.
import { api } from "@/lib/api";

/**
 * THE currency this practice prices and charges in.
 *
 * WHY THIS IS NOT A PER-ROW CHOICE. A practice chooses its operating currency when its
 * account is created and every price it sets is denominated in that one. The catalogue
 * forms used to render a fixed CAD/USD/EUR/INR selector, of which at most one value was
 * ever settleable — the others produced a product that could be advertised, bought, and
 * never booked. So the forms STATE the currency; they no longer offer one.
 *
 * NOT THE SETTLEMENT CURRENCY, which is what the platform's ledger books and what a
 * payout leaves in. A Canadian practice charges CAD and is paid USD; Stripe converts at
 * capture. Screens that show a practitioner's BALANCE read the settlement currency
 * (`gateway/contract.js`), and screens that show a PRICE read this.
 *
 * ONE FETCH PER SESSION, per tenant — a browser session is inside exactly one practice
 * (the host decides which), so the in-flight promise is shared: four catalogue screens
 * mounting at once make one request, and a screen mounted later reuses the value.
 *
 * `null` MEANS "NOT KNOWN YET", NOT "NONE". Callers render nothing rather than guessing —
 * in production the reverse proxy answers an unknown /api path with 200 + index.html, so a
 * payload is only believed when it really carries a three-letter code.
 */
let inFlight = null;
let resolved = null;

const looksLikeCurrency = (v) =>
  typeof v === "string" && /^[a-z]{3}$/.test(v.trim().toLowerCase());

export function fetchTenantCurrency() {
  if (resolved) return Promise.resolve(resolved);
  if (!inFlight) {
    inFlight = api
      .get("/public/stripe-connect/config")
      .then(({ data }) => {
        // `tenant_currency` first; `settlement_currency` is the answer a server that
        // predates the field gives, and it is the right one for a practice that operates
        // in the platform's own currency — which every practice did until this shipped.
        const raw = (data && (data.tenant_currency || data.settlement_currency)) || null;
        const code = looksLikeCurrency(raw) ? raw.trim().toLowerCase() : null;
        resolved = code;
        return code;
      })
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

export default function useTenantCurrency() {
  const [currency, setCurrency] = useState(resolved);
  useEffect(() => {
    let alive = true;
    fetchTenantCurrency().then((code) => {
      if (alive) setCurrency(code);
    });
    return () => {
      alive = false;
    };
  }, []);
  return currency;
}

/** For tests that need to start from a clean slate. */
export function __resetTenantCurrencyCache() {
  inFlight = null;
  resolved = null;
}
